import React, { useState, useEffect } from 'react';
import { customerService } from '../services/customerService';
import BaseModal from './common/BaseModal';
import api from '../services/api';

const CustomerHistoryModal = ({ customerId, customerName, customer, isOpen, onClose }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('receipts');
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && customerId) {
      loadHistory();
    }
  }, [isOpen, customerId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await customerService.getHistory(customerId);
      if (response.success) {
        setHistory(response.data);
      } else {
        setError(response.message || 'Blad ladowania historii');
      }
    } catch (err) {
      console.error('Blad ladowania historii:', err);
      setError('Nie udalo sie zaladowac historii klienta');
    } finally {
      setLoading(false);
    }
  };

  const loadReceiptDetails = async (receiptId) => {
    try {
      setDetailsLoading(true);
      const response = await api.get(`/customers/${customerId}/receipts/${receiptId}`);
      const data = response.data;
      if (data.success) {
        setItemDetails(data.data);
        setSelectedItem({ type: 'receipt', id: receiptId });
      }
    } catch (err) {
      console.error('Blad ladowania szczegolow paragonu:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const loadOrderDetails = async (orderId) => {
    try {
      setDetailsLoading(true);
      const response = await api.get(`/customers/${customerId}/orders/${orderId}`);
      const data = response.data;
      if (data.success) {
        setItemDetails(data.data);
        setSelectedItem({ type: 'order', id: orderId });
      }
    } catch (err) {
      console.error('Blad ladowania szczegolow zamowienia:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL');
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2) + ' zl';
  };

  // Czy karta pelni role dostawcy - decyduje o tresci komunikatu przy pustej liscie FZ/PZ.
  // Powiazanie idzie po kontrahent_id (twarde), wiec pusto oznacza "nic nie przypieto",
  // a nie "nie jest dostawca".
  const jestDostawca = !!Number(customer?.jest_dostawca ?? history?.customer?.jest_dostawca ?? 0);

  const emptyDostawcaInfo = (czegoBrak) => (
    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px', fontSize: '12px' }}>
      {jestDostawca
        ? `Brak ${czegoBrak} przypiętych do tej karty`
        : `Brak ${czegoBrak} (kontrahent nie jest oznaczony jako dostawca)`}
    </p>
  );

  const getStatusBadge = (status) => {
    const statusMap = {
      'zakonczony': { color: 'var(--success)', text: 'Zakonczony' },
      'w_trakcie': { color: 'var(--warning)', text: 'W trakcie' },
      'anulowany': { color: 'var(--danger)', text: 'Anulowany' },
      'pending': { color: 'var(--warning)', text: 'Oczekujace' },
      'confirmed': { color: 'var(--info)', text: 'Potwierdzone' },
      'processing': { color: 'var(--info)', text: 'W realizacji' },
      'completed': { color: 'var(--success)', text: 'Zrealizowane' },
      'cancelled': { color: 'var(--danger)', text: 'Anulowane' }
    };
    const config = statusMap[status] || { color: 'var(--text-muted)', text: status };
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
        backgroundColor: config.color + '20',
        color: config.color
      }}>
        {config.text}
      </span>
    );
  };

  const footerContent = (
    <button
      onClick={onClose}
      style={{
        padding: '10px 24px',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        backgroundColor: 'var(--card-bg)',
        cursor: 'pointer',
        fontSize: '14px'
      }}
    >
      Zamknij
    </button>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="📋 Historia klienta"
      subtitle={customerName || `Klient #${customerId}`}
      size="xl"
      zIndex={10000}
      fullHeight
      noPadding
      headerStyle={{
        padding: '20px',
        backgroundColor: 'var(--card-bg)',
        color: 'var(--text)',
        borderBottom: '1px solid var(--border)',
      }}
      footer={footerContent}
    >
      {/* Stats */}
      {history?.stats && (
        <div style={{
          padding: '15px 20px',
          backgroundColor: 'var(--bg)',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <div>
            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Paragony</span>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--success)' }}>
              {history.stats.total_receipts} ({formatCurrency(history.stats.total_spent_receipts)})
            </div>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Zamowienia</span>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--info)' }}>
              {history.stats.total_orders} ({formatCurrency(history.stats.total_spent_orders)})
            </div>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Faktury</span>
            <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--purple)' }}>
              {history.stats.total_invoices} ({formatCurrency(history.stats.total_spent_invoices)})
            </div>
          </div>
          {history.stats.total_purchase_invoices > 0 && (
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Faktury zakupu</span>
              <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--warning)' }}>
                {history.stats.total_purchase_invoices} ({formatCurrency(history.stats.total_purchase_value)})
              </div>
            </div>
          )}
          {history.stats.total_pz > 0 && (
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>PZ</span>
              <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--success)' }}>
                {history.stats.total_pz} ({formatCurrency(history.stats.total_pz_value)})
              </div>
            </div>
          )}
          {history.stats.total_coupons > 0 && (
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Kupony</span>
              <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--danger)' }}>
                {history.stats.total_coupons} ({formatCurrency(history.stats.total_coupons_value)})
              </div>
            </div>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Laczne wydatki</span>
            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text)' }}>
              {formatCurrency(history.stats.total_spent)}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--border)',
        padding: '0 20px',
        overflowX: 'auto'
      }}>
        {[
          { id: 'receipts', label: '🧾 Paragony', count: history?.receipts?.length || 0 },
          { id: 'orders', label: '📦 Zamowienia', count: history?.orders?.length || 0 },
          { id: 'invoices', label: '📄 Faktury', count: history?.invoices?.length || 0 },
          { id: 'purchase_invoices', label: '📋 FZ', count: history?.purchase_invoices?.length || 0 },
          { id: 'pz', label: '📥 PZ', count: history?.pz_documents?.length || 0 },
          { id: 'coupons', label: '🎟️ Kupony', count: (history?.coupons?.length || 0) + (history?.purchased_coupons?.length || 0) }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSelectedItem(null);
              setItemDetails(null);
            }}
            style={{
              padding: '12px 20px',
              
              background: activeTab === tab.id ? 'var(--card-bg)' : 'transparent',
              borderBottom: activeTab === tab.id ? '2px solid var(--info)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--info)' : 'var(--text-muted)',
              fontWeight: activeTab === tab.id ? '600' : '400',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Lista */}
        <div style={{
          flex: selectedItem ? '0 0 50%' : '1',
          overflowY: 'auto',
          padding: '15px 20px',
          borderRight: selectedItem ? '1px solid var(--border)' : 'none'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              Ladowanie historii...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--danger)' }}>
              {error}
            </div>
          ) : (
            <>
              {/* Paragony */}
              {activeTab === 'receipts' && (
                <div>
                  {history?.receipts?.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px' }}>
                      Brak paragonow
                    </p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--bg)' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Numer</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Kwota</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Platnosc</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Pozycji</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {history?.receipts?.map(receipt => (
                          <tr
                            key={receipt.id}
                            style={{
                              borderBottom: '1px solid var(--border)',
                              backgroundColor: selectedItem?.type === 'receipt' && selectedItem?.id === receipt.id ? 'var(--info-light)' : 'transparent',
                              cursor: 'pointer'
                            }}
                            onClick={() => loadReceiptDetails(receipt.id)}
                          >
                            <td style={{ padding: '10px', fontWeight: '500' }}>{receipt.numer}</td>
                            <td style={{ padding: '10px' }}>{formatDate(receipt.data)} {receipt.czas}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(receipt.suma_brutto)}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              {receipt.forma_platnosci === 'gotowka' ? '💵' : receipt.forma_platnosci === 'karta' ? '💳' : '📱'}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{receipt.liczba_pozycji}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); loadReceiptDetails(receipt.id); }}
                                style={{
                                  padding: '4px 8px', fontSize: '11px', border: '1px solid var(--border)',
                                  borderRadius: '4px', backgroundColor: 'var(--card-bg)', cursor: 'pointer'
                                }}
                              >
                                Szczegoly
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Zamowienia */}
              {activeTab === 'orders' && (
                <div>
                  {history?.orders?.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px' }}>
                      Brak zamowien
                    </p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--bg)' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Numer</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Kwota</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Status</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Pozycji</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {history?.orders?.map(order => (
                          <tr
                            key={order.id}
                            style={{
                              borderBottom: '1px solid var(--border)',
                              backgroundColor: selectedItem?.type === 'order' && selectedItem?.id === order.id ? 'var(--info-light)' : 'transparent',
                              cursor: 'pointer'
                            }}
                            onClick={() => loadOrderDetails(order.id)}
                          >
                            <td style={{ padding: '10px', fontWeight: '500' }}>{order.numer}</td>
                            <td style={{ padding: '10px' }}>{formatDate(order.data)}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(order.wartosc_brutto)}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{getStatusBadge(order.status)}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{order.liczba_pozycji}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); loadOrderDetails(order.id); }}
                                style={{
                                  padding: '4px 8px', fontSize: '11px', border: '1px solid var(--border)',
                                  borderRadius: '4px', backgroundColor: 'var(--card-bg)', cursor: 'pointer'
                                }}
                              >
                                Szczegoly
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Faktury */}
              {activeTab === 'invoices' && (
                <div>
                  {history?.invoices?.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px' }}>
                      Brak faktur
                    </p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--bg)' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Numer</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Kwota</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Typ</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history?.invoices?.map(invoice => (
                          <tr key={invoice.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>{invoice.numer}</td>
                            <td style={{ padding: '10px' }}>
                              {formatDate(invoice.data)} {invoice.godzina || ''}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(invoice.wartosc_brutto)}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{invoice.typ || 'VAT'}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              {invoice.oplacona ? (
                                <span style={{ color: 'var(--success)' }}>Oplacona</span>
                              ) : (
                                <span style={{ color: 'var(--warning)' }}>Nieoplacona</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Faktury zakupu (FZ) */}
              {activeTab === 'purchase_invoices' && (
                <div>
                  {/* Podsumowanie wartosci zakupow - jesli backend je policzyl */}
                  {history?.stats?.total_purchase_value !== undefined && history?.purchase_invoices?.length > 0 && (
                    <div style={{
                      marginBottom: '10px', padding: '8px 12px', borderRadius: '4px',
                      backgroundColor: 'var(--warning-light)', color: 'var(--warning)',
                      fontSize: '12px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px'
                    }}>
                      <span>Faktury zakupu przypięte do tej karty: <strong>{history.stats.total_purchase_invoices ?? history.purchase_invoices.length}</strong></span>
                      <span>Łączna wartość: <strong>{formatCurrency(history.stats.total_purchase_value)}</strong></span>
                    </div>
                  )}
                  {!history?.purchase_invoices?.length ? (
                    emptyDostawcaInfo('faktur zakupu')
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--warning-light)' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Numer FZ</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Netto</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>VAT</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Brutto</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Status</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Lokalizacja</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history?.purchase_invoices?.map(inv => (
                          <tr key={inv.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>{inv.numer}</td>
                            <td style={{ padding: '10px' }}>{formatDate(inv.data)}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{formatCurrency(inv.wartosc_netto)}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{formatCurrency(inv.wartosc_vat)}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(inv.wartosc_brutto)}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{getStatusBadge(inv.status)}</td>
                            <td style={{ padding: '10px' }}>{inv.lokalizacja || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* PZ */}
              {activeTab === 'pz' && (
                <div>
                  {!history?.pz_documents?.length ? (
                    emptyDostawcaInfo('dokumentów PZ')
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--success-light)' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Numer PZ</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Typ</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Wartosc</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Pozycji</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Status</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Lokalizacja</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history?.pz_documents?.map(pz => (
                          <tr key={pz.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '10px', fontWeight: '500' }}>📥 {pz.numer}</td>
                            <td style={{ padding: '10px' }}>{formatDate(pz.data)}</td>
                            <td style={{ padding: '10px' }}>{pz.typ || 'PZ'}</td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(pz.wartosc)}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{pz.liczba_pozycji || '-'}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>{getStatusBadge(pz.status)}</td>
                            <td style={{ padding: '10px' }}>{pz.lokalizacja || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Kupony */}
              {activeTab === 'coupons' && (
                <div>
                  {history?.purchased_coupons?.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '10px' }}>🛒 Zakupione kupony</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--danger-light)' }}>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Numer</th>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Kod</th>
                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Wartosc</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid var(--border)' }}>Platnosc</th>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data zakupu</th>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Wazny do</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history?.purchased_coupons?.map(coupon => (
                            <tr key={coupon.id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '10px', fontWeight: '500' }}>{coupon.numer}</td>
                              <td style={{ padding: '10px', fontFamily: 'monospace', fontWeight: '600', color: 'var(--danger)' }}>{coupon.kod}</td>
                              <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(coupon.wartosc)}</td>
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                {coupon.forma_platnosci === 'gotowka' ? '💵' : coupon.forma_platnosci === 'karta' ? '💳' : '📱'}
                              </td>
                              <td style={{ padding: '10px' }}>{formatDate(coupon.data)}</td>
                              <td style={{ padding: '10px' }}>{coupon.data_waznosci || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {history?.coupons?.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '10px' }}>Wykorzystane kupony</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--info-light)' }}>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Kod kuponu</th>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Paragon</th>
                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Wykorzystano</th>
                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--border)' }}>Pozostalo</th>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Data</th>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Kasjer</th>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Sklep</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history?.coupons?.map(coupon => (
                            <tr key={coupon.id} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '10px', fontFamily: 'monospace', fontWeight: '600', color: 'var(--info)' }}>{coupon.kod}</td>
                              <td style={{ padding: '10px' }}>{coupon.numer_paragonu || '-'}</td>
                              <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>{formatCurrency(coupon.kwota_wykorzystana)}</td>
                              <td style={{ padding: '10px', textAlign: 'right', color: coupon.pozostala_wartosc > 0 ? 'var(--info)' : 'var(--text-muted)' }}>
                                {formatCurrency(coupon.pozostala_wartosc)}
                              </td>
                              <td style={{ padding: '10px' }}>{formatDate(coupon.data)} {coupon.czas || ''}</td>
                              <td style={{ padding: '10px' }}>{coupon.kasjer || '-'}</td>
                              <td style={{ padding: '10px' }}>{coupon.lokalizacja || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(!history?.coupons?.length && !history?.purchased_coupons?.length) && (
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '30px' }}>
                      Brak kuponow
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Szczegoly */}
        {selectedItem && (
          <div style={{ flex: '0 0 50%', overflowY: 'auto', padding: '15px 20px', backgroundColor: 'var(--bg)' }}>
            {detailsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                Ladowanie szczegolow...
              </div>
            ) : itemDetails ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>
                    {selectedItem.type === 'receipt' ? '🧾 Paragon' : '📦 Zamowienie'}: {itemDetails.numer}
                  </h3>
                  <button
                    onClick={() => { setSelectedItem(null); setItemDetails(null); }}
                    style={{
                      padding: '4px 8px', fontSize: '12px', border: '1px solid var(--border)',
                      borderRadius: '4px', backgroundColor: 'var(--card-bg)', cursor: 'pointer'
                    }}
                  >
                    Zamknij
                  </button>
                </div>

                <div style={{
                  backgroundColor: 'var(--card-bg)', padding: '15px', borderRadius: '8px',
                  marginBottom: '15px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Data:</span>
                      <span style={{ marginLeft: '10px', fontWeight: '500' }}>
                        {formatDate(itemDetails.data)} {itemDetails.czas || ''}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Suma brutto:</span>
                      <span style={{ marginLeft: '10px', fontWeight: '600', color: 'var(--success)' }}>
                        {formatCurrency(itemDetails.suma_brutto || itemDetails.wartosc_brutto)}
                      </span>
                    </div>
                    {itemDetails.forma_platnosci && (
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Platnosc:</span>
                        <span style={{ marginLeft: '10px', fontWeight: '500' }}>{itemDetails.forma_platnosci}</span>
                      </div>
                    )}
                    {itemDetails.kasjer && (
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Kasjer:</span>
                        <span style={{ marginLeft: '10px' }}>{itemDetails.kasjer}</span>
                      </div>
                    )}
                  </div>
                </div>

                <h4 style={{ fontSize: '14px', margin: '0 0 10px' }}>Pozycje:</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', overflow: 'hidden' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--bg)' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>#</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Produkt</th>
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid var(--border)' }}>Ilosc</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Cena</th>
                      <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Wartosc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemDetails.pozycje?.map((item, idx) => (
                      <tr key={item.id || idx} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px' }}>{item.lp || idx + 1}</td>
                        <td style={{ padding: '8px', fontWeight: '500' }}>{item.nazwa || item.nazwa_produktu}</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>{item.ilosc} {item.jednostka || 'szt'}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(item.cena)}</td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.wartosc_brutto)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: 'var(--bg)' }}>
                      <td colSpan="4" style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>SUMA:</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '700', fontSize: '14px', color: 'var(--success)' }}>
                        {formatCurrency(itemDetails.suma_brutto || itemDetails.wartosc_brutto)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default CustomerHistoryModal;
